/**
 * Moderacja imion (jedyny publiczny tekst) — KOMPLETNY, ZAMROŻONY moduł WP0.
 *
 * PLAN §17.2 + PLAN_REVIEW2 korekta 4:
 * WP1 (profile) i WP4 (companion) korzystają z TEGO SAMEGO interfejsu.
 * Implementacja tu (shared) — zero zależności runtime WP1 → WP4.
 */

export type ModerationRejectReason =
  | 'too_short'
  | 'too_long'
  | 'invalid_chars'
  | 'too_many_spaces'
  | 'profanity'
  | 'pii'
  | 'empty';

export type ModerationResult =
  | { ok: true; normalized: string }
  | { ok: false; reason: ModerationRejectReason; suggestion: string };

/** Interfejs — WP1/WP4 importują z @pieskowo/shared. */
export interface NameModerator {
  /** Normalizacja NFKC + trim + collapse spacji. */
  normalize(raw: string): string;
  /** Pełna walidacja; przy reject zwraca wygenerowane imię. */
  validate(raw: string): ModerationResult;
  /** Generator bezpiecznego imienia (fallback). */
  generateName(seed?: number): string;
}

const MIN_LEN = 2;
const MAX_LEN = 12;
const ALLOWED = /^[a-ząćęłńóśźżA-ZĄĆĘŁŃÓŚŹŻ0-9 -]+$/u;

/** Fold PL diakrytyków + leet → a-z do matchingu filtrów. */
export function foldForFilter(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[0o]/g, 'o')
    .replace(/[1i!|]/g, 'i')
    .replace(/[3e]/g, 'e')
    .replace(/[4a@]/g, 'a')
    .replace(/[5s$]/g, 's')
    .replace(/[7t]/g, 't')
    .replace(/[^a-z]/g, '');
}

/**
 * Lista wulgaryzmów PL+EN (złożona, po foldingu).
 * Świadomie konserwatywna — fałszywe pozytywy OK (dziecko dostanie sugestię).
 */
export const BAD_WORDS_FOLDED: readonly string[] = [
  // PL
  'kurwa',
  'chuj',
  'cipa',
  'dupa',
  'jebac',
  'jebany',
  'pierdol',
  'spierdalaj',
  'sukinsyn',
  'debil',
  'idiota',
  'fuck',
  'shit',
  'bitch',
  'asshole',
  'dick',
  'pussy',
  'cunt',
  'nigger',
  'faggot',
  'sex',
  'porn',
  'nude',
  // minimal leet variants already covered by fold
];

const SAFE_NAMES = [
  'Misia',
  'Lola',
  'Bella',
  'Luna',
  'Pusia',
  'Tosia',
  'Nela',
  'Gucia',
  'Fela',
  'Mila',
  'Zosia',
  'Hania',
  'Ruda',
  'Figa',
  'Kira',
  'Nala',
] as const;

const SYLLABLES_A = ['Mi', 'Lo', 'Be', 'Lu', 'Pu', 'To', 'Ne', 'Gu', 'Fe', 'Ki', 'Na', 'Ru'] as const;
const SYLLABLES_B = ['sia', 'la', 'lla', 'na', 'sia', 'sia', 'la', 'cia', 'la', 'ra', 'la', 'da'] as const;

export function normalizeName(raw: string): string {
  let s = raw.normalize('NFKC').trim();
  s = s.replace(/\s+/g, ' ');
  return s;
}

function hasProfanity(normalized: string): boolean {
  const folded = foldForFilter(normalized);
  return BAD_WORDS_FOLDED.some((w) => folded.includes(w));
}

function hasPii(normalized: string): boolean {
  // ciągi cyfr ≥6, @, http/https
  if (/\d{6,}/.test(normalized)) return true;
  if (normalized.includes('@')) return true;
  if (/https?:/i.test(normalized)) return true;
  if (/www\./i.test(normalized)) return true;
  return false;
}

function countSpaces(s: string): number {
  return (s.match(/ /g) ?? []).length;
}

export function generateName(seed?: number): string {
  const r =
    seed !== undefined
      ? () => {
          // LCG
          seed = (Math.imul(seed!, 1664525) + 1013904223) >>> 0;
          return seed / 0x100000000;
        }
      : Math.random;
  // 70% z listy bezpiecznej, 30% sylaby
  if (r() < 0.7) {
    return SAFE_NAMES[Math.floor(r() * SAFE_NAMES.length)]!;
  }
  const a = SYLLABLES_A[Math.floor(r() * SYLLABLES_A.length)]!;
  const b = SYLLABLES_B[Math.floor(r() * SYLLABLES_B.length)]!;
  const name = a + b;
  return name.length > MAX_LEN ? name.slice(0, MAX_LEN) : name;
}

export function validateName(raw: string): ModerationResult {
  const suggestion = generateName();
  const normalized = normalizeName(raw);
  if (!normalized) {
    return { ok: false, reason: 'empty', suggestion };
  }
  if (normalized.length < MIN_LEN) {
    return { ok: false, reason: 'too_short', suggestion };
  }
  if (normalized.length > MAX_LEN) {
    return { ok: false, reason: 'too_long', suggestion };
  }
  if (!ALLOWED.test(normalized)) {
    return { ok: false, reason: 'invalid_chars', suggestion };
  }
  if (countSpaces(normalized) > 1) {
    return { ok: false, reason: 'too_many_spaces', suggestion };
  }
  if (hasPii(normalized)) {
    return { ok: false, reason: 'pii', suggestion };
  }
  if (hasProfanity(normalized)) {
    return { ok: false, reason: 'profanity', suggestion };
  }
  return { ok: true, normalized };
}

/** Domyślna implementacja interfejsu. */
export const nameModerator: NameModerator = {
  normalize: normalizeName,
  validate: validateName,
  generateName,
};
